import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { range } from '@app/shared/utils/util';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faLeftLong } from '@fortawesome/pro-solid-svg-icons/faLeftLong';
import { faRightLong } from '@fortawesome/pro-solid-svg-icons/faRightLong';

export const PAGE_GROUP_SIZE = 3; // Number of pages to show between first and last page
export const PAGE_GROUP_ENABLED_MIN = 6; // Need at least this many pages to use grouping

@Component({
	selector: 'g-paginator',
	templateUrl: './paginator.component.html',
	styleUrls: ['./paginator.component.scss'],
})
export class PaginatorComponent implements OnInit, OnChanges {
	@Input() currentPage = 1;
	@Input() pageCount = 1;
	@Output() readonly onChange = new EventEmitter();
	pages: number[];
	showPageSetControlsPrev: boolean;
	showPageSetControlsNext: boolean;
	icons = {
		faChevronLeft,
		faLeftLong,
		faRightLong,
		faChevronRight,
	};

	showPagination = false;

	// Contains pages 1...n-1, first and last pages are special cases
	private allPages: readonly number[] = [];
	private pagesIdx: number;

	constructor() {}

	ngOnInit(): void {
		this.pages = [];
		this.showPagination = this.pageCount > 1;
		this.allPages = range(1, this.pageCount + 1);
		this.buildPages();
	}

	ngOnChanges(changes: SimpleChanges): void {
		let shouldRebuildPages = false;
		if (changes.currentPage?.previousValue !== changes.currentPage?.currentValue) {
			shouldRebuildPages = true;
		}

		if (changes.pageCount?.previousValue !== changes.pageCount?.currentValue) {
			const pageCount = changes.pageCount.currentValue as number;
			this.showPagination = pageCount > 1;
			this.allPages = range(1, pageCount + 1);
			shouldRebuildPages = true;
		}

		if (shouldRebuildPages) this.buildPages();
	}

	selectAdjacentPage(direction: number): void {
		if (
			(this.currentPage === 1 && direction === -1) ||
			(this.currentPage === this.pageCount && direction === 1)
		)
			return;
		this.currentPage += direction;
		this.onChange.emit(this.currentPage);
	}

	selectPage(page: number): void {
		if (this.currentPage === page || page < 1 || page > this.pageCount) return;
		this.currentPage = page;
		this.onChange.emit(page);
	}

	showPageSet(direction: number): void {
		const startIdx = Math.max(1, this.pagesIdx + PAGE_GROUP_SIZE * direction);
		const endIdx = Math.min(this.pageCount - 2, startIdx + PAGE_GROUP_SIZE - 1);

		this.pages = this.allPages.slice(startIdx, endIdx + 1);
		this.pagesIdx = startIdx;

		this.showPageSetControlsPrev = startIdx > 1 && this.pageCount > PAGE_GROUP_SIZE;
		this.showPageSetControlsNext = endIdx < this.pageCount - 2;
	}

	private buildPages(): void {
		const currentPageIdx = this.currentPage - 1;
		const startIdx =
			this.pageCount > PAGE_GROUP_ENABLED_MIN
				? Math.max(1, currentPageIdx - Math.floor(PAGE_GROUP_SIZE / 2))
				: 1;
		const endIdx = Math.min(this.pageCount - 2, startIdx + PAGE_GROUP_SIZE - 1);

		this.pages = this.allPages.slice(startIdx, endIdx + 1);
		this.pagesIdx = startIdx;

		this.showPageSetControlsPrev = startIdx > 1;
		this.showPageSetControlsNext = endIdx < this.pageCount - 2;
	}
}
